import { ButtonBase } from '@mui/material';
import { alpha, keyframes, styled } from '@mui/material/styles';
import { bool, func, string } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';

const vcGrow = keyframes`
  from { transform: scaleX(.4) scaleY(.84); opacity: 0.15; }
  to { transform: none; opacity: 1; }
`;

const StopButtonRoot = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'grow',
})(({ theme, grow }) => {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;

  return {
    height: 38,
    borderRadius: '999px',
    padding: '0 16px 0 14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '9px',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    backgroundColor: alpha(primary, isDark ? 0.18 : 0.1),
    border: `1px solid ${alpha(primary, 0.5)}`,
    color: isDark ? theme.palette.primary.light || '#aeb2ff' : primary,
    transition: 'background-color .18s ease, border-color .18s ease',
    '&:hover': {
      backgroundColor: alpha(primary, isDark ? 0.26 : 0.16),
      borderColor: alpha(primary, 0.7),
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha(primary, 0.6)}`,
      outlineOffset: 2,
    },
    ...(grow && {
      animation: `${vcGrow} 420ms cubic-bezier(.2,.8,.25,1) both`,
      transformOrigin: 'right center',
    }),
    '@media (prefers-reduced-motion: reduce)': {
      ...(grow && { animation: 'none' }),
    },
  };
});

const Bars = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  height: 18,
});

const Bar = styled('span')({
  width: 3.5,
  borderRadius: '999px',
  backgroundColor: 'currentColor',
  minHeight: 5,
  transition: 'height .08s linear',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
});

const Bar0 = styled(Bar)({
  height: 'calc((0.3 + var(--vc-b0, 0) * 0.7) * 18px)',
});

const Bar1 = styled(Bar)({
  height: 'calc((0.3 + var(--vc-b1, 0) * 0.7) * 18px)',
});

const Bar2 = styled(Bar)({
  height: 'calc((0.3 + var(--vc-b2, 0) * 0.7) * 18px)',
});

const Label = styled('span')({
  whiteSpace: 'nowrap',
});

const StopButton = ({ onClick, label, grow = false }) => {
  const text = label || I18n.t('VoiceMode.stop');

  return (
    <StopButtonRoot type="button" grow={grow} onClick={onClick} aria-label={text} title={text}>
      <Bars aria-hidden="true">
        <Bar0 />
        <Bar1 />
        <Bar2 />
      </Bars>
      <Label>{text}</Label>
    </StopButtonRoot>
  );
};

StopButton.propTypes = {
  onClick: func,
  label: string,
  grow: bool,
};

export default StopButton;
